<template>
  <div class="other-filter">
    <div class="dropdown-filter">
      <filter-multiselect
        v-model="selectedValues"
        track-by="key"
        label="label"
        :disabled="loading"
        :mobile-modal="false"
        :top-left-label="filterMeta?.name"
        :loading="loading"
        :options="dropdownOptions">
        <template #action-buttons="{ isOpen }">
          <div class="quick-action-buttons">
            <b-button
              class="quick-action-button"
              type="is-text"
              :disabled="loading"
              @click.stop="filterChange(null)">
              All
            </b-button>
            <b-button
              class="quick-action-button"
              type="is-text"
              :disabled="loading"
              @click.stop="filterChange([])">
              None
            </b-button>
            <b-tooltip
              v-if="activeActivityFilterCount > 0 && isOpen"
              type="is-dark"
              position="is-bottom"
              multilined>
              <template #content>
                This filter's options are refined by {{ activeActivityFilterCount }} condition{{ activeActivityFilterCount > 1 ? 's' : '' }}
              </template>
              <i class="material-icons-outlined condition-info-icon">
                info
              </i>
            </b-tooltip>
          </div>
        </template>
      </filter-multiselect>
    </div>
    <SnapQLDebugFilterButton
      :filter-key="filterMeta.key"
      :data-context="fulldataContext"
      :dashboard-key="dashboardKey" />
  </div>
</template>

<script>
  import { debounce } from 'lodash'
  import filterMultiselect from '@/components/ui/multiselect'
  import SnapQLDebugFilterButton from './SnapQLDebugFilterButton.vue'

  export default {
    name: 'CustomFilter',
    components: {
      filterMultiselect,
      SnapQLDebugFilterButton,
    },
    mixins: [],
    props: {
      filterMeta: {
        type: Object,
        required: true,
      },
      dashboardKey: {
        type: String,
        required: true,
      },
      value: {
        type: Array,
        required: false,
        default: null,
      },
      fulldataContext: {
        type: Object,
        required: true,
      },
      ignoreSelectedDates: {
        type: Boolean,
        default: true,
      },
    },
    data () {
      return {
        loading: false,
        dropdownOptions: [],
        activeActivityFilterCount: 0,
      }
    },
    computed: {
      selectedValues: {
        get () {
          return this.value === null ? this.dropdownOptions : this.dropdownOptions.filter(o => this.value?.includes(o.key)) || []
        },
        set (newValue) {
          this.$emit(
            'input',
            newValue?.length === this.dropdownOptions?.length ? null : newValue?.map(value => value.key)
          )

          if (!this.loading) {
            this.$emit('makeInputsDirty')
          }
        },
      },
      selectedDatesKey () {
        return JSON.stringify(this.fulldataContext?.date_selection?.selected_dates || [])
      },
      benchmarkSelectionsKey () {
        const cs = this.fulldataContext?.control_selections || {}
        const benchmarks = {}
        for (const [key, val,] of Object.entries(cs)) {
          if (val?.type === 'BenchmarkSelect') {
            benchmarks[key] = val?.selected_option
          }
        }
        return JSON.stringify(benchmarks)
      },
    },
    watch: {
      selectedDatesKey () {
        if (!this.ignoreSelectedDates) {
          this.debouncedReload()
        }
      },
      benchmarkSelectionsKey () {
        this.debouncedReload()
      },
    },
    created () {
      this.debouncedReload = debounce(this.reloadOptions, 300)
    },
    async mounted () {
      await this.reloadOptions()
    },
    beforeDestroy () {
      if (this.debouncedReload) {
        this.debouncedReload.cancel()
      }
    },
    methods: {
      async reloadOptions () {
        this.loading = true
        const useSnapQL = this.$hasFeatureFlag('USE_SNAPQL')
        if (useSnapQL) {
          await this.$apis.companyConfigs.streamDashboardFilterOptions(
            this.dashboardKey,
            this.filterMeta.key,
            this.fulldataContext,
            {
              options: (options) => {
                this.dropdownOptions = options || []
              },
              complete: async () => {
                await this.$nextTick()
                this.loading = false
              },
              error: () => {
                this.loading = false
              },
            }
          )
        } else {
          const response = await this.$apis.companyConfigs.getDashboardFilterOptionsWithMetadata(
            this.dashboardKey,
            this.filterMeta.key,
            this.fulldataContext
          )
          this.dropdownOptions = response?.options || []
          this.activeActivityFilterCount = response?.activity_filter_count ?? 0
          await this.$nextTick()
          this.loading = false
        }
      },
      filterChange (values) {
        this.selectedValues = values
      },
    },
  }
</script>
<style lang="scss" scoped>

::v-deep .dropdown-menu {
  .dropdown-content {
    margin-top: 4px !important;
    border-radius: 6px !important;
  }
}

::v-deep .filter-button {
  &.filtered {
    border-color: #994095 !important;
    .top-left-label {
      color: #994095 !important;
    }
    .button-content-bottom {
      .button-value, .dropdown-icon {
        color: $grayscale-1 !important;
      }
    }
  }

  .quick-action-buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 5px;
    width: 100%;
  }
  .quick-action-button {
    font-size: 11px;
    color: #504f54 !important;
    padding: 0 !important;
    font-weight: 300;
    border-radius: 6px;
    box-shadow: none !important;
    height: 20px !important;
    padding: 0px 5px !important;
    background-color: $grayscale-7 !important;
  }

  .condition-info-icon {
    margin-left: auto;
    font-size: 16px;
    color: #994095;
    cursor: default;
  }
}

.dropdown-content {
  border-radius: 4px !important;
}
</style>
